import { EnvConfig } from '@config';
import { useUpdateEffect } from '@react-hookz/web';
import identity from 'lodash/identity';
import queryString from 'query-string';
import { DependencyList, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
// import { loadedAppInsights } from 'services/appInsights/appInsights';
import superagent, { SuperAgentRequest } from 'superagent';
import { ApiError } from 'types/apiResponse';
import { v1 as uuidv1 } from 'uuid';

import authConfigInstance from './authServise';
import { getUserToken } from './tokenManager';

export enum Method {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  PATCH = 'patch',
  DELETE = 'delete',
}

export type UseFetchOptions<ResponseType> = {
  method?: Method;
  /**
   * if autoFetch is false, doFetch will never be called automatically
   * if autoFetch is true (or becomes true), doFetch will be called automatically
   * if autoFetch is an array, doFetch will be called automatically if the values in the list change
   *
   * Note: if autoFetch is not false, doFetch will be called each time `url` changes
   */
  autoFetch?: boolean | DependencyList;
  // If true, responseBody will be reset before sending a new request
  resetResponseBeforeFetching?: boolean;
  // if provided, responseBody will be reset each time the values in the list change
  resetResponseDependencyList?: DependencyList;
  responseFormatCallback?: (response: any) => ResponseType;
};

type DoFetchParams = {
  relativeUrl?: string;
  queryParams?: Record<string, unknown>;
  body?: Record<string, unknown>;
  formData?: Record<string, string>;
  files?: Record<string, File[]>;
};

type DoFetchReturnType<ResponseType> = Promise<{
  responseBody?: ResponseType;
  error?: ApiError;
}>;

enum ActionType {
  INIT,
  SUCCESS,
  ERROR,
  RESET_RESPONSE,
  MANUALLY_ABORTED,
}

export const useFetch = <ResponseType>(
  url: string,
  options: UseFetchOptions<ResponseType> = {},
): {
  isLoading: boolean;
  responseBody?: ResponseType;
  error?: ApiError;
  doFetch: (doFetchParams?: DoFetchParams) => DoFetchReturnType<ResponseType>;
  abortRequest: () => void;
  resetResponse: () => void;
} => {
  type Action =
    | { type: ActionType.INIT }
    | { type: ActionType.SUCCESS; responseBody?: ResponseType }
    | { type: ActionType.ERROR; error: ApiError }
    | { type: ActionType.RESET_RESPONSE }
    | { type: ActionType.MANUALLY_ABORTED };

  type State = {
    isLoading: boolean;
    responseBody?: ResponseType;
    error?: ApiError;
  };

  const method = options.method ?? Method.GET;
  const responseFormatCallback = options.responseFormatCallback ?? identity;

  const initialState: State = useMemo(() => ({ isLoading: false }), []);

  const reducer = useCallback(
    (state: State, action: Action): State => {
      const { isLoading, responseBody } = state;
      switch (action.type) {
        case ActionType.INIT:
          return options.resetResponseBeforeFetching
            ? { isLoading: true }
            : { isLoading: true, responseBody };
        case ActionType.SUCCESS:
          return { isLoading: false, responseBody: action.responseBody };
        case ActionType.ERROR:
          return { isLoading: false, error: action.error };
        case ActionType.RESET_RESPONSE:
          return state === initialState ? state : { isLoading };
        case ActionType.MANUALLY_ABORTED:
          return state === initialState ? state : { isLoading: false, responseBody };
      }
    },
    [initialState, options.resetResponseBeforeFetching],
  );

  const requestRef = useRef<SuperAgentRequest>();

  const [state, dispatch] = useReducer(reducer, initialState);

  const abortRequest = useCallback(() => requestRef.current?.abort(), []);

  const manuallyAbortRequest = useCallback(() => {
    abortRequest();
    dispatch({ type: ActionType.MANUALLY_ABORTED });
  }, [abortRequest]);

  const resetResponse = useCallback(() => dispatch({ type: ActionType.RESET_RESPONSE }), []);

  const doFetch = useCallback(
    async ({
      relativeUrl,
      queryParams,
      body,
      formData,
      files,
    }: DoFetchParams = {}): DoFetchReturnType<ResponseType> => {
      dispatch({ type: ActionType.INIT });

      abortRequest();

      let fullUrl = url;

      if (relativeUrl) fullUrl += relativeUrl;

      if (queryParams) {
        const separator = fullUrl.includes('?') ? '&' : '?';
        fullUrl += `${separator}${queryString.stringify(queryParams, { arrayFormat: 'bracket' })}`;
      }

      const request = superagent[method](fullUrl);

      requestRef.current = request;

      request.accept('application/json');

      try {
        const token = await getUserToken();
        if (token) request.set('Authorization', `Bearer ${token}`);
        if (EnvConfig.apim_subscription_key)
          request.set('Ocp-Apim-Subscription-Key', EnvConfig.apim_subscription_key);
        if (EnvConfig.apim_trace) request.set('Ocp-Apim-Trace', EnvConfig.apim_trace);
        // cache control
        request.set('Cache-Control', 'no-cache');
        request.set('Cache-Control', 'max-age=0');
        // set the request id from pseudo random number generator and timestamp
        request.set('x-request-id', uuidv1());
        // @ts-expect-error in case of the request is aborted
        if (request._aborted) return {};

        if (method === Method.POST && (formData || files)) {
          for (const key in formData) {
            request.field(key, formData[key]);
          }
          for (const key in files) {
            files[key].forEach((file: any) => request.attach(key, file));
          }
        } else if ([Method.POST, Method.PUT, Method.PATCH].includes(method) && body) {
          request.send(body);
        }

        const response = await request;
        // log if any error
        if (!response.ok) {
          // TODO
          dispatch({ type: ActionType.ERROR, error: response.body });

          return { error: response.body };
        }
        const responseBody = response.body;
        dispatch({ type: ActionType.SUCCESS, responseBody: responseFormatCallback(responseBody) });

        return { responseBody };
      } catch (error: any) {
        if (error?.status === 401) {
          console.warn('401 Unauthorized — redirecting to login');
          await authConfigInstance.signIn();

          return { error };
        }
        if (error.code !== 'ABORTED') {
          dispatch({ type: ActionType.ERROR, error });
        }

        return { error };
      }
    },
    [abortRequest, method, url, responseFormatCallback],
  );

  const autoFetchDependencies = [url].concat(
    Array.isArray(options.autoFetch) ? options.autoFetch : [options.autoFetch],
  );

  useEffect(() => {
    if (!options.autoFetch) return;
    doFetch();

    return abortRequest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, autoFetchDependencies);

  useEffect(() => {
    return abortRequest; // abort request when component is unmounted in case of manual fetching with doFetch
  }, [abortRequest]);

  useUpdateEffect(resetResponse, options.resetResponseDependencyList ?? []);

  return { ...state, doFetch, abortRequest: manuallyAbortRequest, resetResponse };
};
